import { BOUNDARY_BASE_PATH } from '../constants.js';

/**
 * Extend MapApp with boundary loading, editing, and overlay helpers.
 * @param {typeof import('../app.js').default} App
 */
export function applyBoundaryMixins(App) {
	/**
	 * Show the boundary overlay for a given country, fetching GeoJSON if needed.
	 * @param {object} country
	 */
	App.prototype.activateBoundaryOverlay = function activateBoundaryOverlay(country) {
		if (!this.boundaryLayer || !country) {
			return;
		}

		const slug = country.boundary;
		if (!slug) {
			this.clearBoundaryOverlay();
			return;
		}

		this.boundaryRequestId += 1;
		const requestId = this.boundaryRequestId;

		if (this.activeBoundarySlug !== slug) {
			this.clearBoundaryOverlay();
		}

		const cached = this.boundaryCache[slug];
		if (cached) {
			if (requestId === this.boundaryRequestId) {
				this.renderBoundaryData(country, slug, cached);
			}
			return;
		}

		const geoJsonPath = `${BOUNDARY_BASE_PATH}/${slug}.geojson`;
		fetch(geoJsonPath)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Boundary not found: ${geoJsonPath}`);
				}
				return response.json();
			})
			.then((data) => {
				this.boundaryCache[slug] = data;
				if (requestId !== this.boundaryRequestId) {
					return;
				}
				this.renderBoundaryData(country, slug, data);
			})
			.catch((error) => {
				if (requestId !== this.boundaryRequestId) {
					return;
				}
				console.warn(error);
				this.clearBoundaryOverlay();
			});
	};

	/**
	 * Render GeoJSON data into the boundary layer and panel.
	 * @param {object} country
	 * @param {string} slug
	 * @param {GeoJSON} data
	 */
	App.prototype.renderBoundaryData = function renderBoundaryData(country, slug, data) {
		if (!this.boundaryLayer || !data) {
			return;
		}
		this.boundaryLayer.clearLayers();
		this.boundaryLayer.addData(data);
		this.activeBoundarySlug = slug;
		if (this.isEditMode()) {
			this.setupBoundaryEditHandles(country, slug, data);
		} else {
			this.teardownBoundaryEditHandles();
		}
		this.applyBoundaryGeoJsonToPanel(slug, data);
	};

	/**
	 * Remove the boundary overlay and any edit handles.
	 */
	App.prototype.clearBoundaryOverlay = function clearBoundaryOverlay() {
		if (!this.boundaryLayer) {
			return;
		}
		this.boundaryLayer.clearLayers();
		this.activeBoundarySlug = null;
		this.teardownBoundaryEditHandles();
	};

	/**
	 * Create draggable edit handles for a boundary GeoJSON polygon.
	 * @param {object} country
	 * @param {string} slug
	 * @param {GeoJSON} geojson
	 */
	App.prototype.setupBoundaryEditHandles = function setupBoundaryEditHandles(
		country,
		slug,
		geojson
	) {
		if (!this.isEditMode() || !this.boundaryControlLayer || !geojson || !country || !slug) {
			this.teardownBoundaryEditHandles();
			return;
		}
		this.teardownBoundaryEditHandles();
		const coordRefs = this.extractBoundaryCoordinateRefs(geojson);
		if (!coordRefs.length) {
			this.boundaryEditState = {
				slug,
				country,
				geojson,
				handles: []
			};
			return;
		}
		const icon = this.getBoundaryHandleIcon();
		const handles = [];
		for (let i = 0; i < coordRefs.length; i += 1) {
			const ref = coordRefs[i];
			const latlng = globalThis.L.latLng(ref.coordinate[1], ref.coordinate[0]);
			const marker = globalThis.L.marker(latlng, {
				draggable: true,
				icon,
				pane: this.boundaryHandlePane,
				zIndexOffset: 500
			});
			marker.on('drag', (event) => {
				this.onBoundaryHandleMove(ref, event);
			});
			marker.on('dragend', (event) => {
				this.onBoundaryHandleMove(ref, event);
			});
			marker.addTo(this.boundaryControlLayer);
			ref.marker = marker;
			handles.push(ref);
		}
		this.boundaryEditState = {
			slug,
			country,
			geojson,
			handles
		};
		this.updateBoundaryGeoJsonPreview();
	};

	/**
	 * Remove boundary edit handles and clear edit state.
	 */
	App.prototype.teardownBoundaryEditHandles = function teardownBoundaryEditHandles() {
		if (this.boundaryControlLayer) {
			this.boundaryControlLayer.clearLayers();
		}
		this.boundaryEditState = null;
	};

	/**
	 * Re-render the boundary layer from the current edit state.
	 */
	App.prototype.refreshBoundaryLayerFromEditState =
		function refreshBoundaryLayerFromEditState() {
			if (!this.boundaryEditState || !this.boundaryEditState.geojson || !this.boundaryLayer) {
				return;
			}
			this.boundaryLayer.clearLayers();
			this.boundaryLayer.addData(this.boundaryEditState.geojson);
			if (this.boundaryEditState.slug) {
				this.boundaryCache[this.boundaryEditState.slug] = this.boundaryEditState.geojson;
			}
		};

	/**
	 * Walk GeoJSON features to produce references to editable coordinates.
	 * @param {GeoJSON} geojson
	 * @returns {Array}
	 */
	App.prototype.extractBoundaryCoordinateRefs =
		function extractBoundaryCoordinateRefs(geojson) {
			const refs = [];
			const features = this.normalizeGeoJsonFeatures(geojson);
			for (let featureIndex = 0; featureIndex < features.length; featureIndex += 1) {
				const feature = features[featureIndex];
				if (!feature || !feature.geometry) {
					continue;
				}
				const geometry = feature.geometry;
				if (geometry.type === 'Polygon') {
					this.pushPolygonRefs(refs, geometry.coordinates, featureIndex);
				} else if (geometry.type === 'MultiPolygon') {
					const polygons = geometry.coordinates;
					if (!Array.isArray(polygons)) {
						continue;
					}
					for (let polygonIndex = 0; polygonIndex < polygons.length; polygonIndex += 1) {
						this.pushPolygonRefs(refs, polygons[polygonIndex], featureIndex, polygonIndex);
					}
				}
			}
			return refs;
		};

	/**
	 * Normalize arbitrary GeoJSON input into an array of Feature objects.
	 * @param {GeoJSON} geojson
	 * @returns {Array}
	 */
	App.prototype.normalizeGeoJsonFeatures = function normalizeGeoJsonFeatures(geojson) {
		if (!geojson) {
			return [];
		}
		if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features)) {
			return geojson.features;
		}
		if (geojson.type === 'Feature') {
			return [geojson];
		}
		if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
			return [{ type: 'Feature', geometry: geojson }];
		}
		return [];
	};

	/**
	 * Push references for each coordinate in a polygon.
	 * @param {Array} refs
	 * @param {Array} polygonCoords
	 * @param {number} featureIndex
	 * @param {number} [polygonIndex=0]
	 */
	App.prototype.pushPolygonRefs = function pushPolygonRefs(
		refs,
		polygonCoords,
		featureIndex,
		polygonIndex
	) {
		if (!Array.isArray(polygonCoords)) {
			return;
		}
		const polyIndex = typeof polygonIndex === 'number' ? polygonIndex : 0;
		for (let ringIndex = 0; ringIndex < polygonCoords.length; ringIndex += 1) {
			const ring = polygonCoords[ringIndex];
			if (!Array.isArray(ring)) {
				continue;
			}
			for (let coordIndex = 0; coordIndex < ring.length; coordIndex += 1) {
				const coordinate = ring[coordIndex];
				if (!Array.isArray(coordinate) || coordinate.length < 2) {
					continue;
				}
				if (this.isClosingCoordinate(ring, coordIndex)) {
					continue;
				}
				refs.push({
					coordinate,
					ring,
					indices: {
						featureIndex,
						polygonIndex: polyIndex,
						ringIndex,
						coordIndex
					}
				});
			}
		}
	};

	/**
	 * Determine whether a coordinate closes a ring (duplicate of first point).
	 */
	App.prototype.isClosingCoordinate = function isClosingCoordinate(ring, coordIndex) {
		if (!Array.isArray(ring) || ring.length < 2) {
			return false;
		}
		if (coordIndex !== ring.length - 1) {
			return false;
		}
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (!Array.isArray(first) || !Array.isArray(last)) {
			return false;
		}
		return first[0] === last[0] && first[1] === last[1];
	};

	/**
	 * Update GeoJSON coordinates when a boundary handle is moved.
	 * @param {object} handleRef
	 * @param {L.LeafletEvent} event
	 */
	App.prototype.onBoundaryHandleMove = function onBoundaryHandleMove(handleRef, event) {
		if (!handleRef || !handleRef.coordinate) {
			return;
		}
		const marker = handleRef.marker;
		const latlng =
			(event && event.latlng) ||
			(marker && typeof marker.getLatLng === 'function'
				? marker.getLatLng()
				: null);
		if (!latlng) {
		 return;
		}
		handleRef.coordinate[0] = latlng.lng;
		handleRef.coordinate[1] = latlng.lat;
		this.syncClosingCoordinate(handleRef);
		this.refreshBoundaryLayerFromEditState();
		this.updateBoundaryGeoJsonPreview();
	};

	/**
	 * Keep ring closing coordinates in sync when an end point moves.
	 */
	App.prototype.syncClosingCoordinate = function syncClosingCoordinate(handleRef) {
		if (!handleRef || !handleRef.ring || !handleRef.indices) {
			return;
		}
		const ring = handleRef.ring;
		if (!Array.isArray(ring) || ring.length < 2) {
			return;
		}
		if (handleRef.indices.coordIndex === 0) {
			const last = ring[ring.length - 1];
			if (Array.isArray(last)) {
				last[0] = handleRef.coordinate[0];
				last[1] = handleRef.coordinate[1];
			}
		} else if (handleRef.indices.coordIndex === ring.length - 1) {
			const first = ring[0];
			if (Array.isArray(first)) {
				first[0] = handleRef.coordinate[0];
				first[1] = handleRef.coordinate[1];
			}
		}
	};

	/**
	 * Create or reuse the Leaflet icon for boundary edit handles.
	 * @returns {L.DivIcon}
	 */
	App.prototype.getBoundaryHandleIcon = function getBoundaryHandleIcon() {
		if (this.boundaryHandleIcon) {
			return this.boundaryHandleIcon;
		}
		this.boundaryHandleIcon = globalThis.L.divIcon({
			className: 'boundary-handle-icon',
			html:
				'<span style="display:block;width:12px;height:12px;border:2px solid #ff9800;background:rgba(255,152,0,0.4);border-radius:50%;box-shadow:0 0 4px rgba(0,0,0,0.4);"></span>',
			iconSize: [16, 16],
			iconAnchor: [8, 8]
		});
		return this.boundaryHandleIcon;
	};

	/**
	 * Update the JSON preview block in the panel when edits occur.
	 */
	App.prototype.updateBoundaryGeoJsonPreview = function updateBoundaryGeoJsonPreview() {
		if (!this.boundaryEditState || !this.boundaryEditState.geojson) {
			return;
		}
		this.applyBoundaryGeoJsonToPanel(
			this.boundaryEditState.slug,
			this.boundaryEditState.geojson
		);
	};

	/**
	 * Inject GeoJSON text into the boundary preview area if present.
	 */
	App.prototype.applyBoundaryGeoJsonToPanel = function applyBoundaryGeoJsonToPanel(slug, geojson) {
		if (!geojson || typeof document === 'undefined') {
			return;
		}
		const previewEl = document.getElementById('boundary_geojson_preview');
		if (!previewEl) {
			return;
		}
		const previewSlug = previewEl.getAttribute('data-boundary-slug');
		if (previewSlug && slug && previewSlug !== slug) {
			return;
		}
		const target = previewEl.querySelector('[data-json-payload="boundary"]') || previewEl;
		target.textContent = JSON.stringify(geojson, null, 2);
		const placeholder = previewEl.querySelector('[data-json-placeholder-for="boundary"]');
		if (placeholder) {
			placeholder.remove();
		}
		if (typeof this.setJsonCopyReadyState === 'function') {
			this.setJsonCopyReadyState('boundary', true);
		}
	};

	/**
	 * Reconstruct edit handles for the currently active boundary when needed.
	 */
	App.prototype.refreshBoundaryEditHandles = function refreshBoundaryEditHandles() {
		if (!this.isEditMode() || !this.activeBoundarySlug) {
			this.teardownBoundaryEditHandles();
			return;
		}
		const data = this.boundaryCache[this.activeBoundarySlug];
		const country = this.findCountryByBoundarySlug(this.activeBoundarySlug);
		if (!data || !country) {
			this.teardownBoundaryEditHandles();
			return;
		}
		this.setupBoundaryEditHandles(country, this.activeBoundarySlug, data);
	};

	/**
	 * Locate the country object whose boundary slug matches.
	 * @param {string} slug
	 * @returns {object|null}
	 */
	App.prototype.findCountryByBoundarySlug =
		function findCountryByBoundarySlug(slug) {
			if (!slug || !Array.isArray(globalThis.countries)) {
				return null;
			}
			for (let i = 0; i < globalThis.countries.length; i += 1) {
				const country = globalThis.countries[i];
				if (country && country.boundary === slug) {
					return country;
				}
			}
			return null;
		};
}
